// THE single resolver for "which backend is this console talking to".
//
// There used to be two: this generic derivation (in backOfficeApi) and a second,
// stale copy inside authClient.js gated on a hardcoded 8-entry BACKEND_DOMAINS
// allowlist. They agreed on tkt.ug/fanpass and disagreed everywhere else:
//
//   backoffice.moments.ug → backOfficeApi: backend.moments.ug | authClient: backend.tkt.ug
//   backoffice.mookh.com  → backOfficeApi: backend.mookh.com  | authClient: backend.tkt.ug
//
// The session cookie lives on the host backOfficeApi authenticated against, so every
// better-auth SDK call (admin.createUser, organization.inviteMember, the sign-in OTP
// itself) went to a DIFFERENT registrable domain with no cookie — better-auth's
// /admin/create-user answers a session-less request with 401 UNAUTHORIZED (a real
// admin who merely lacks the permission gets 403), surfacing in the console as a
// bare "Could not create user". One resolver, one answer, no drift.
//
// Derivation is generic — backoffice.<domain> → backend.<domain> — so a new product
// domain works without an allowlist edit. Local/preview hosts have no backend.<domain>
// sibling, so they fall back to the build-time override.
const isLocalOrPreviewHost = (hostname) =>
  hostname === 'localhost' ||
  hostname === '127.0.0.1' ||
  hostname === '0.0.0.0' ||
  /^(\d{1,3}\.){3}\d{1,3}$/.test(hostname) || // bare IP
  hostname.endsWith('.local') ||
  hostname.endsWith('.workers.dev');          // CF preview mirror has no backend.<domain>

// "which <sub>.<domain> sibling of the host we're served on" — the ONE place that idiom
// lives. The console derives three of them: backend.<domain> (API), apis.<domain> (the
// ticket-PDF pass service) and ws.<domain> (the WhatsApp socket, which now proxies to the
// API's own port rather than a listener of its own). They were three hand-copied blocks
// sharing one private predicate, which is how deleting that predicate took out two call
// sites nobody had grepped for. `fallback` covers local/preview hosts, where no such
// sibling exists; `ws` swaps http(s) → ws(s).
export const derivedHostUrl = (sub, fallback, { ws = false } = {}) => {
  if (typeof window !== 'undefined' && window.location?.host) {
    const { protocol, host } = window.location;
    const hostname = host.split(':')[0]; // strip any :port
    if (!isLocalOrPreviewHost(hostname)) {
      const parts = hostname.split('.');
      // "<sub>.<domain.tld>" → strip the first label; a bare apex domain is used as-is.
      const domain = parts.length > 2 ? parts.slice(1).join('.') : hostname;
      const scheme = ws ? (protocol === 'https:' ? 'wss:' : 'ws:') : protocol;
      return `${scheme}//${sub}.${domain}`;
    }
  }
  return typeof fallback === 'function' ? fallback() : fallback;
};

export const getBackendUrl = () =>
  derivedHostUrl('backend', () => import.meta.env.VITE_TKT_BACKEND_URL || 'http://127.0.0.1:3001');

// ─── EVERY CROSS-HOST URL THIS CONSOLE BUILDS, IN ONE PLACE ──────────────────────────────────
//
// The header above records what happens otherwise: two resolvers for "which host", disagreeing on
// every domain but two, and a session cookie written to one of them. The same class had already
// put a hardcoded `tkt.ug/e/{slug}` in six places — a 404 on every non-tkt deployment.
//
// So a host is named here or it is not built. `scripts/check-host-literals.mjs` fails the build on
// a product-host literal anywhere else in src/, which is the only thing that keeps this list the
// single copy rather than the first of several.
//
// Two kinds live here, and the difference is the whole point:
//   · DERIVED   — a per-deployment sibling of the console's own host.
//   · FIXED     — one shared host for every tenant, with the evidence for why.

/** The storefront / organiser site. tkt-frontend deploys to NEXT_PUBLIC_CLIENT_URL=front.ticket.ug;
 *  the back office links out to pages that live there (the public admissions view). DERIVED. */
export const getFrontUrl = () =>
  derivedHostUrl('front', () => import.meta.env.VITE_TKT_FRONT_URL || null);

/** Uploaded documents and artwork (KYC files, event posters). DERIVED — each deployment stores its
 *  own uploads. NOTE the two spellings live in prod data: `EventPage.photo` holds
 *  media.ticket.ug URLs while the KYC screens were hardcoding media.tkt.ug. A stored absolute URL
 *  is used as-is; this is only for composing one from a key. */
export const getMediaUrl = () =>
  derivedHostUrl('media', () => import.meta.env.VITE_TKT_MEDIA_URL || 'https://media.tkt.ug');

/** The AI/MCP server this console's operators add to Claude. DERIVED — each brand runs its own,
 *  talking to its own backend, exactly as mcp.<domain> does (ai.fanpass.ke and mcp.fanpass.ke both
 *  resolve; the hardcoded ai.tkt.ug this replaced would have handed a fanpass operator a URL that
 *  authenticates against the wrong backend). Not every brand has one deployed yet — that is a
 *  deployment gap, and pointing at the brand's own address is how it stays visible as one. */
export const getAiUrl = () =>
  derivedHostUrl('ai', () => import.meta.env.VITE_TKT_AI_URL || 'https://ai.tkt.ug');

/** FIXED — the legacy PHP app at ticket.ug that resolves a Meta media id to the actual bytes
 *  (`/replies/mediaget.php`, answering 200 today). Inbound WhatsApp media has only a Meta id, and
 *  Meta's own URL is ephemeral and needs our access token, so no browser can fetch it directly.
 *  tkt-frontend's whatsapp-formatter already points here; there is no per-brand sibling, because
 *  the token this endpoint holds belongs to one WhatsApp number. Deriving it would 404. */
export const getWhatsAppMediaProxyUrl = () =>
  import.meta.env.VITE_TKT_WA_MEDIA_PROXY_URL || 'https://ticket.ug';

/** The accreditation invitation page a guest opens from their card. DERIVED. */
export const getInvitationUrl = () =>
  derivedHostUrl('i', () => import.meta.env.VITE_TKT_INVITATION_URL || 'https://i.tkt.ug');

/** The BUYER-facing storefront an affiliate link points at — the apex, not a sibling. Derivation
 *  cannot produce it (there is no `buy.<domain>`), so it is the console's own registrable domain:
 *  the host with its first label stripped.
 *
 *  IT REUSES `isLocalOrPreviewHost` RATHER THAN RESTATING IT. The first version inlined its own
 *  copy of that predicate and, in copying, dropped the bare-IP case — so on a LAN address like
 *  `192.168.1.20:5173` it saw three labels, stripped the first, and returned `http://168.1.20`.
 *  Affiliate tracking links and the TSPL sample QR are built from this. That is the whole bug this
 *  file exists to end, committed inside the file that ends it: a predicate with two copies has two
 *  behaviours, and the second one is always the one nobody tested. */
export const getConsumerUrl = () => {
  if (typeof window !== 'undefined' && window.location?.host) {
    const { protocol, host } = window.location;
    const hostname = host.split(':')[0];
    const parts = hostname.split('.');
    if (parts.length > 2 && !isLocalOrPreviewHost(hostname)) {
      return `${protocol}//${parts.slice(1).join('.')}`;
    }
  }
  return import.meta.env.VITE_TKT_CONSUMER_URL || 'https://tkt.ug';
};
